import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { getValidToken } from "../../utils/tokenManager";
import { useContactContext } from "../../contexts/ContactContext";
import Sidebar from "../common/Sidebar";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import finish from "../../assets/agent/End Day Icon.svg";
import searchIcon from "../../assets/common/search-icon.svg";
import AgentHeader from "./commonSections.jsx/AgentHeader";
import ContactInfo from "./commonSections.jsx/ContactInfo";
import NoteInfo from "./commonSections.jsx/NoteInfo";
import AgentWorkspaceMobile from "./mobile/AgentWorkspaceMobile";
import axios from "axios";

// Maps the URL segment (same convention NoteInfo.jsx already reads from
// the path) to the array already exposed by ContactContext, and to a
// label/color for the filter pills.
const CALL_TYPE_TABS = [
  { param: "cold-calls", labelKey: "agent.cards.coldCalls", activeClass: "bg-red-100 text-red-700 ring-2 ring-offset-1 ring-red-400" },
  { param: "follow-up", labelKey: "agent.cards.FollowUp", activeClass: "bg-amber-100 text-amber-700 ring-2 ring-offset-1 ring-amber-400" },
  { param: "meeting", labelKey: "agent.cards.Meeting", activeClass: "bg-green-100 text-green-700 ring-2 ring-offset-1 ring-green-400" },
  { param: "meeting-recovery", labelKey: "agent.cards.MeetingRecovery", activeClass: "bg-blue-100 text-blue-700 ring-2 ring-offset-1 ring-blue-400" },
];

function AgentWorkspace() {
  const { callType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    coldCallContacts,
    followUpContacts,
    meetingContacts,
    meetingRecovery,
    loading,
  } = useContactContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("agentSidebarPinned");
    return saved === "true";
  });
  const clinentId = sessionStorage.getItem("clinentId");

  const listsByType = {
    "cold-calls": coldCallContacts,
    "follow-up": followUpContacts,
    meeting: meetingContacts,
    "meeting-recovery": meetingRecovery,
  };

  // Search by name, email, or company - across the full list of the
  // active tab. "fullList" is derived inside the memo so the hook's
  // dependency array can list the actual context arrays.
  const filteredList = useMemo(() => {
    const fullList = listsByType[callType] || [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return fullList;
    return fullList.filter((c) => {
      const company = (c.company || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const fullName = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      return company.includes(term) || email.includes(term) || fullName.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callType, coldCallContacts, followUpContacts, meetingContacts, meetingRecovery, searchTerm]);

  const selectedContact =
    filteredList.find((c) => c.contactId === selectedContactId) ||
    filteredList[0] ||
    null;

  // Keep the selection valid whenever the list changes (tab switch,
  // search, or a refresh after submitting a call result via NoteInfo).
  // Also this is what actually advances to the next contact: NoteInfo
  // clears selectedContactId after a successful submit, which makes this
  // effect fall through to picking filteredList[0] - the next contact
  // waiting in the queue.
  useEffect(() => {
    if (!filteredList.some((c) => c.contactId === selectedContactId)) {
      setSelectedContactId(filteredList[0]?.contactId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredList]);

  const fetchClientDetails = useCallback(async (signal) => {
    if (!clinentId) return;
    try {
      const accessToken = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_APIBASEURL}/api/clients/${clinentId}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal }
      );
      if (!signal?.aborted) setClientDetails(response.data);
    } catch (err) {
      if (!axios.isCancel(err)) console.error("Error fetching client details:", err);
    }
  }, [clinentId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchClientDetails(controller.signal);
    return () => controller.abort();
  }, [fetchClientDetails]);

  return (
    <>
    <div className="hidden lg:flex lg:flex-row min-h-screen bg-secondaryBg">
      <Sidebar
        logoIcon={logoIcon}
        logout={logout}
        finish={finish}
        labelClassName="bg-secondary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />

      <div
        className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-7 my-10 transition-all duration-300 ${
          sidebarPinned ? "lg:ml-[250px]" : "lg:ml-[80px]"
        }`}
      >
        <div className="max-w-[1500px] mx-auto w-full">
          <AgentHeader />

          <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr_300px] gap-4 mt-2">
            {/* Column 1 - assigned leads for the active tab, searchable by
                name, email, or company */}
            <div className="bg-white rounded-lg shadow-md p-4 h-fit">
              <h3 className="text-lg font-semibold mb-3 text-primary">
                {t("agent.workspace.leads", "Leads")}
              </h3>

              <div className="flex items-center gap-2 bg-lightBlue rounded-md px-3 py-2 mb-3">
                <img src={searchIcon} alt="search" className="w-4 h-4 opacity-60 flex-shrink-0" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t(
                    "agent.workspace.searchPlaceholder",
                    "Nombre, correo o empresa..."
                  )}
                  className="bg-transparent flex-1 min-w-0 text-sm outline-none text-primary placeholder-gray-400"
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {CALL_TYPE_TABS.map((tab) => (
                  <button
                    key={tab.param}
                    onClick={() => {
                      setSearchTerm("");
                      navigate(`/agent/workspace/${tab.param}`);
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                      tab.param === callType
                        ? tab.activeClass
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                    }`}
                  >
                    {t(tab.labelKey)}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-2 max-h-[65vh] overflow-y-auto">
                {!loading && filteredList.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">
                    {t("agent.workspace.noContacts", "No hay contactos en esta categoria")}
                  </p>
                )}
                {filteredList.map((c) => {
                  const isSelected = c.contactId === selectedContact?.contactId;
                  return (
                    <button
                      key={c.contactId}
                      onClick={() => setSelectedContactId(c.contactId)}
                      className={`text-left rounded-md px-3 py-2 transition-all ${
                        isSelected
                          ? "bg-lightBlue border-l-4 border-primary"
                          : "hover:bg-gray-50 border-l-4 border-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-primary truncate">
                          {c.firstName} {c.lastName}
                        </p>
                        <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-tertiary/20 text-primary font-medium">
                          {c.attempts} {t("agent.noteInfo.attempt")}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{c.company || "-"}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Column 2 - selected contact's full data card. Info and
                notes-history icons live inside ContactInfo's own header. */}
            <div>
              {selectedContact ? (
                <ContactInfo contact={selectedContact} clientDetails={clientDetails} callType={callType} />
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-400 h-full flex items-center justify-center min-h-[300px]">
                  {t("agent.workspace.selectContact", "Selecciona un contacto de la lista")}
                </div>
              )}
            </div>

            {/* Column 3 - work area to register the call result. Clearing
                the selection (via onNextContact) after a successful save
                lets the correction effect above pick the next contact. */}
            <div>
              {selectedContact ? (
                <NoteInfo
                  contact={selectedContact}
                  onNextContact={() => setSelectedContactId(null)}
                />
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-400 h-full flex items-center justify-center min-h-[300px]">
                  {t("agent.workspace.selectContactResult", "Aqui vera el formulario de resultado")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="lg:hidden">
      <AgentWorkspaceMobile />
    </div>
    </>
  );
}

export default AgentWorkspace;